import type * as unist from 'unist';

import { Block, type Document, type Fragment, Inline, type Text } from '@gitbook/slate';
import { assertNever } from '@gitbook/util-ts';

import type { FormattingContext } from '../context';

/**
 * Function to transform a slate node into unist ones.
 */
export type GastToAstTransformFn<
    NodeType extends Block | Inline | Text = Block | Inline | Text,
    Result = unist.Node,
    ExpectedChildren = unist.Node,
    ParentType extends Document | Block | Inline = Document | Block | Inline,
> = (
    node: NodeType,
    // @ts-expect-error
    eat: (children: NodeType['nodes']) => Promise<Array<ExpectedChildren>>,
    context: FormattingContext,
    parent: ParentType
) => Promise<Array<Result>> | Array<Result>;

/**
 * Map of keys to transform functions.
 */
export type GastToAstTransformMap<
    NodeType extends Block | Inline | Text = Block | Inline | Text,
    Result = unist.Node,
    ExpectedChildren = unist.Node,
    ParentType extends Document | Block | Inline = Document | Block | Inline,
> = {
    [key: string]: GastToAstTransformFn<NodeType, Result, ExpectedChildren, ParentType>;
};

/**
 * Convert a document to a unist AST tree.
 */
export async function gastToAst<Result extends unist.Parent>(
    document: Document | Fragment | Inline | Block,
    context: FormattingContext,
    handlers: {
        transformInlines?: { [type: string]: GastToAstTransformMap<Inline> };
        transformBlocks?: { [type: string]: GastToAstTransformMap<Block> };
        transformText: GastToAstTransformFn<Text>;
    }
): Promise<Result> {
    async function transformBlock(
        block: Block,
        parent: Block | Document | Inline | Fragment
    ): Promise<unist.Node[]> {
        const transform = handlers.transformBlocks?.[block.type];
        if (!transform) {
            return [];
        }

        // @ts-expect-error
        return transform(block, (...args) => transformNodes(...args, block), context, parent);
    }

    async function transformInline(
        inline: Inline,
        parent: Block | Document | Inline | Fragment
    ): Promise<unist.Node[]> {
        const transform = handlers.transformInlines?.[inline.type];
        if (!transform) {
            return [];
        }

        // @ts-expect-error
        return transform(inline, (...args) => transformNodes(...args, inline), context, parent);
    }

    async function transformNode(
        node: Inline | Block | Text,
        parent: Block | Document | Inline | Fragment
    ): Promise<unist.Node[]> {
        switch (node.object) {
            case 'block':
                return transformBlock(node, parent);
            case 'inline':
                return transformInline(node, parent);
            case 'text':
                // @ts-expect-error
                return handlers.transformText(node, async () => [], context, parent);
            default:
                assertNever(node);
        }
    }

    async function transformNodes(
        nodes: Fragment['nodes'] | Block['nodes'] | Document['nodes'] | Inline['nodes'],
        parent: Block | Document | Inline | Fragment
    ): Promise<unist.Node[]> {
        const content: unist.Node[] = [];
        for (const node of nodes) {
            const result = await transformNode(node, parent);
            content.push(...result);
        }
        return content;
    }

    return {
        type: 'root',
        children: await transformNodes(
            // @ts-expect-error
            Inline.isInline(document) || Block.isBlock(document) ? [document] : document.nodes,
            document
        ),
    } as Result;
}
